import MapChart from "./MapChart";
import StateAvailabilityPanel from "./StateAvailabilityPanel";
import LockedStateAvailability from "./LockedStateAvailability";
import React, { useState, useEffect, useMemo, useCallback } from "react";
import styles from "./Stock.module.css";
import { fetchWithAuth } from "../utils/ApiUtils";
import { describeChecked, POLL_SECONDS } from "../utils/lastChecked";
import { useAuth } from "../context/AuthContext";
import { TrackingService } from "../TrackingService";
import {
    Button, Typography, Box, Snackbar, Alert,
    CircularProgress, Tooltip
} from "@mui/material";
import { keyframes } from "@mui/system";
import BookmarkIcon from "@mui/icons-material/Bookmark";
import BookmarkBorderIcon from "@mui/icons-material/BookmarkBorder";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useNavigate, useLocation } from "react-router-dom";
import NotificationPreferences from "./NotificationPreferences";
import { formatCurrency, formatPercent, getDisplayPrice } from "../utils/priceUtils";
import { probeExtension, connectExtension } from "../utils/extensionBridge";
import { categoryNoun } from "../utils/productCategories";
import ExtensionPromptDialog from "./ExtensionPromptDialog";
import TrackGateDialog from "./TrackGateDialog";
import { stateZipCodes } from "../utils/StateZipCodes";
import { bestBuyPickupLocation } from "../utils/pickupStore";
import { ghostCooldownRemaining } from "../utils/ghostCooldown";

/**
 * Checkout-nudge frequency (owner decision, Simplify-style): the prompt
 * shows on EVERY checkout click until the extension is installed. The only
 * silencer is the user's own "Don't show this popup again" checkbox, which
 * persists via localStorage.
 */
const NUDGE_OPT_OUT_KEY = 'stockmaid.checkoutNudge.optOut';
// "Don't show this again" on the track gate. Outlives the tab because it is a
// choice, unlike the skip below which only lasts as long as this page.
const TRACK_GATE_OPT_OUT_KEY = 'stockmaid.trackGate.optOut';
const optedOutOfTrackGate = () => {
    try { return Boolean(window.localStorage.getItem(TRACK_GATE_OPT_OUT_KEY)); } catch (e) { return false; }
};
const optedOutOfNudge = () => {
    try { return Boolean(window.localStorage.getItem(NUDGE_OPT_OUT_KEY)); } catch (e) { return false; }
};

const statusPing = keyframes`
    0% { transform: scale(1); opacity: 0.45; }
    70% { transform: scale(2.1); opacity: 0; }
    100% { transform: scale(2.1); opacity: 0; }
`;

const STATE_NAME_BY_CODE = {
    AL: "Alabama",
    AK: "Alaska",
    AZ: "Arizona",
    AR: "Arkansas",
    CA: "California",
    CO: "Colorado",
    CT: "Connecticut",
    DE: "Delaware",
    FL: "Florida",
    GA: "Georgia",
    HI: "Hawaii",
    ID: "Idaho",
    IL: "Illinois",
    IN: "Indiana",
    IA: "Iowa",
    KS: "Kansas",
    KY: "Kentucky",
    LA: "Louisiana",
    ME: "Maine",
    MD: "Maryland",
    MA: "Massachusetts",
    MI: "Michigan",
    MN: "Minnesota",
    MS: "Mississippi",
    MO: "Missouri",
    MT: "Montana",
    NE: "Nebraska",
    NV: "Nevada",
    NH: "New Hampshire",
    NJ: "New Jersey",
    NM: "New Mexico",
    NY: "New York",
    NC: "North Carolina",
    ND: "North Dakota",
    OH: "Ohio",
    OK: "Oklahoma",
    OR: "Oregon",
    PA: "Pennsylvania",
    RI: "Rhode Island",
    SC: "South Carolina",
    SD: "South Dakota",
    TN: "Tennessee",
    TX: "Texas",
    UT: "Utah",
    VT: "Vermont",
    VA: "Virginia",
    WA: "Washington",
    WV: "West Virginia",
    WI: "Wisconsin",
    WY: "Wyoming"
};

export const Stock = ({ stock }) => {
    const navigate = useNavigate();
    const location = useLocation();

    const [stateData, setStateData] = useState([]);
    const [showExtensionPrompt, setShowExtensionPrompt] = useState(false);
    const [pendingCheckout, setPendingCheckout] = useState(null);
    const [extensionStatus, setExtensionStatus] = useState(null);
    // The pending track gate ({installed}), and whether it has been waved past
    // on this page — asked once, not on every click.
    const [trackGate, setTrackGate] = useState(null);
    const [trackGateConnecting, setTrackGateConnecting] = useState(false);
    const [trackGateSkipped, setTrackGateSkipped] = useState(false);
    const [stateOverview, setStateOverview] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const { isAuthenticated, isPremium, login } = useAuth();

    const [hasTracking, setHasTracking] = useState(false);
    const [trackingData, setTrackingData] = useState(null);
    const [isTrackingLoading, setIsTrackingLoading] = useState(false);
    const [updatingPreferences, setUpdatingPreferences] = useState(false);
    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState('');
    const [snackbarSeverity, setSnackbarSeverity] = useState('success');
    const [currentTime, setCurrentTime] = useState(Date.now());
    const itemLabelDisplay = categoryNoun(stock?.productCategory);

    useEffect(() => {
        const interval = setInterval(() => {
            setCurrentTime(Date.now());
        }, 1000);

        return () => clearInterval(interval);
    }, []);

    // Asked on mount so the checkout click can read a settled answer
    // (onetapapply's pattern; a click-time probe would race the navigation).
    useEffect(() => {
        let cancelled = false;
        probeExtension().then((status) => {
            if (!cancelled && status) {
                setExtensionStatus(status);
            }
        });
        return () => { cancelled = true; };
    }, []);

    const fetchStateAvailability = useCallback(async () => {
        if (!stock || !stock.sku) return;

        try {
            setIsLoading(true);

            const overviewResponse = await fetchWithAuth(`/api/v1/stocks/sku/${stock.sku}/states`);
            // Kept with its SKU, and dropped when the request fails, so a count is
            // never shown for a product it was not asked about (#392).
            if (overviewResponse.ok) {
                const overviewData = await overviewResponse.json();
                setStateOverview({ sku: stock.sku, data: overviewData.data });
            } else {
                setStateOverview(null);
            }

            if (isPremium && isAuthenticated) {
                const response = await fetchWithAuth(`/api/v1/stocks/sku/${stock.sku}/states/premium`);

                if (response.ok) {
                    const apiResponse = await response.json();
                    setStateData(apiResponse.data || []);
                } else {
                    setStateData([]);
                }
            } else {
                setStateData([]);
            }
        } catch (err) {
            console.error(`Error fetching state data: ${err.message}`);
            setStateOverview(null);
        } finally {
            setIsLoading(false);
        }
    }, [stock, isPremium, isAuthenticated]);

    const checkTrackingStatus = useCallback(async () => {
        if (!isAuthenticated || !stock || !stock.sku) return;

        try {
            setIsTrackingLoading(true);
            const isTracked = await TrackingService.isStockTracked(stock.sku);
            setHasTracking(isTracked);

            if (isTracked) {
                const allTrackings = await TrackingService.getUserTrackedStocks();
                const currentTracking = allTrackings.find((t) => t.stockSku === stock.sku);
                setTrackingData(currentTracking || null);
            } else {
                setTrackingData(null);
            }
        } catch (error) {
            console.error("Error checking tracking status:", error);
        } finally {
            setIsTrackingLoading(false);
        }
    }, [isAuthenticated, stock]);

    useEffect(() => {
        fetchStateAvailability();
        checkTrackingStatus();
    }, [fetchStateAvailability, checkTrackingStatus]);

    // What to ask before a Track click the assistant could not act on, or null
    // to just track. Silence is not a no: a null probe means the question was
    // never heard (wrong origin, non-Chromium, no id configured) and never
    // gates. Untracking is never gated.
    const trackGateReason = () => {
        if (hasTracking || trackGateSkipped || optedOutOfTrackGate()) return null;
        if (!extensionStatus) return null;
        if (extensionStatus.installed && extensionStatus.connected) return null;
        return { installed: Boolean(extensionStatus.installed) };
    };

    const handleTrackingButton = async () => {
        if (!isAuthenticated) {
            localStorage.setItem('redirectAfterLogin', `${window.location.pathname}${window.location.search}`);
            login();
            return;
        }

        if (!isPremium) {
            navigate('/subscription');
            return;
        }

        const gate = trackGateReason();
        if (gate) {
            setTrackGate(gate);
            return;
        }

        await performTracking();
    };

    const performTracking = async () => {
        try {
            setIsTrackingLoading(true);

            if (hasTracking) {
                await TrackingService.removeTracking(stock.sku);
                setHasTracking(false);
                setTrackingData(null);
                showSnackbar(`${itemLabelDisplay.charAt(0).toUpperCase() + itemLabelDisplay.slice(1)} untracked successfully`, 'success');
            } else {
                await TrackingService.createTracking(stock.sku);
                setHasTracking(true);
                await checkTrackingStatus();
                showSnackbar(`${itemLabelDisplay.charAt(0).toUpperCase() + itemLabelDisplay.slice(1)} tracked successfully! You'll be notified when it becomes available.`, 'success');
            }
        } catch (error) {
            console.error('Error with tracking operation:', error);
            showSnackbar(error.message || 'Error with tracking operation', 'error');
        } finally {
            setIsTrackingLoading(false);
        }
    };

    const handlePreferenceChange = async (stockSku, emailInStore, emailOnline) => {
        setUpdatingPreferences(true);

        try {
            await TrackingService.updateNotificationPreferences(
                stockSku,
                emailInStore,
                emailOnline
            );

            await checkTrackingStatus();

            showSnackbar('Notification preferences updated successfully', 'success');
        } catch (error) {
            console.error('Error updating preferences:', error);
            showSnackbar('Failed to update notification preferences', 'error');
        } finally {
            setUpdatingPreferences(false);
        }
    };

    const handleMaxPriceChange = async (stockSku, maxPrice) => {
        setUpdatingPreferences(true);

        try {
            await TrackingService.updateMaxAlertPrice(stockSku, maxPrice);
            await checkTrackingStatus();
            showSnackbar(
                maxPrice === null ? "Maximum price cleared - you'll be emailed at any price" : 'Maximum price saved',
                'success'
            );
        } catch (error) {
            console.error('Error updating maximum price:', error);
            showSnackbar(error.message || 'Failed to update the maximum price', 'error');
        } finally {
            setUpdatingPreferences(false);
        }
    };

    const showSnackbar = (message, severity = 'success') => {
        setSnackbarMessage(message);
        setSnackbarSeverity(severity);
        setSnackbarOpen(true);
    };

    const handleCloseSnackbar = () => {
        setSnackbarOpen(false);
    };

    const mcAvailableStores = useMemo(() => {
        const stores = stock?.mcOffer?.storeAvailabilities;
        if (!Array.isArray(stores)) {
            return [];
        }
        return stores.filter((store) => store && store.inStock && store.state);
    }, [stock]);

    const mcAvailableStateCodes = useMemo(() => {
        const states = stock?.mcOffer?.stateAvailabilities;
        if (!Array.isArray(states)) {
            return [];
        }
        return states
            .filter((state) => state && state.available && state.stateCode)
            .map((state) => String(state.stateCode).trim().toUpperCase());
    }, [stock]);

    const stateCodeByName = useMemo(() => {
        return Object.entries(STATE_NAME_BY_CODE).reduce((acc, [stateCode, stateName]) => {
            acc[stateName.toLowerCase()] = stateCode;
            return acc;
        }, {});
    }, []);

    const bestBuyStateData = useMemo(() => {
        return Array.isArray(stateData) ? stateData : [];
    }, [stateData]);

    const microcenterStateData = useMemo(() => {
        const statesByName = new Map();

        mcAvailableStateCodes.forEach((stateCode) => {
            const normalizedStateCode = String(stateCode || '').trim().toUpperCase();
            const stateName = STATE_NAME_BY_CODE[normalizedStateCode];
            if (!stateName) {
                return;
            }

            statesByName.set(stateName.toLowerCase(), {
                stateName,
                stateCode: normalizedStateCode,
                available: true,
                hasBestBuy: false,
                hasMicroCenter: true,
                zipCode: stateZipCodes[stateName] || ''
            });
        });

        mcAvailableStores.forEach((store) => {
            const rawState = String(store.state || '').trim();
            if (!rawState) {
                return;
            }

            const normalizedStateCode = rawState.length === 2
                ? rawState.toUpperCase()
                : stateCodeByName[rawState.toLowerCase()] || '';
            const normalizedStateName = rawState.length === 2
                ? (STATE_NAME_BY_CODE[normalizedStateCode] || rawState)
                : rawState;
            const key = normalizedStateName.toLowerCase();
            const existing = statesByName.get(key) || {
                stateName: normalizedStateName,
                stateCode: normalizedStateCode,
                available: false,
                hasBestBuy: false,
                hasMicroCenter: true,
                zipCode: ''
            };

            statesByName.set(key, {
                ...existing,
                available: true,
                stateCode: existing.stateCode || normalizedStateCode,
                zipCode: store.zipCode || existing.zipCode || stateZipCodes[normalizedStateName] || '',
                // First in-stock store wins; the map popover deep-links MC
                // checkout to it via ?storeid=.
                mcStoreId: existing.mcStoreId || store.storeId || '',
                mcStoreName: existing.mcStoreName || store.storeName || ''
            });
        });

        return Array.from(statesByName.values())
            .sort((a, b) => a.stateName.localeCompare(b.stateName));
    }, [mcAvailableStateCodes, mcAvailableStores, stateCodeByName]);

    const mergedStateData = useMemo(() => {
        const mergedByState = new Map();

        bestBuyStateData.forEach((state) => {
            if (!state?.stateName) {
                return;
            }
            const key = state.stateName.toLowerCase();
            const normalizedCode = state.stateCode ? String(state.stateCode).trim().toUpperCase() : '';
            // The store that has the stock beats the state's fixed ZIP (#333).
            const pickup = bestBuyPickupLocation(state, stateZipCodes);
            mergedByState.set(key, {
                stateName: state.stateName,
                stateCode: normalizedCode,
                available: !!state.available,
                hasBestBuy: !!state.available,
                hasMicroCenter: false,
                zipCode: pickup.zipCode
            });
        });

        microcenterStateData.forEach((state) => {
            const key = state.stateName.toLowerCase();
            const existing = mergedByState.get(key) || {
                stateName: state.stateName,
                stateCode: state.stateCode || '',
                available: false,
                hasBestBuy: false,
                hasMicroCenter: false,
                zipCode: ''
            };

            mergedByState.set(key, {
                ...existing,
                available: existing.available || !!state.available,
                hasMicroCenter: existing.hasMicroCenter || !!state.available,
                stateCode: existing.stateCode || state.stateCode || '',
                zipCode: existing.zipCode || state.zipCode || stateZipCodes[state.stateName] || '',
                mcStoreId: state.mcStoreId || existing.mcStoreId || '',
                mcStoreName: state.mcStoreName || existing.mcStoreName || ''
            });
        });

        return Array.from(mergedByState.values())
            .sort((a, b) => a.stateName.localeCompare(b.stateName));
    }, [bestBuyStateData, microcenterStateData]);

    const renderAvailabilityPill = (label, isAvailable) => (
        <Box
            sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 0.75,
                px: 1,
                py: 0.35,
                borderRadius: 999,
                bgcolor: isAvailable ? 'rgba(34,197,94,0.14)' : 'rgba(239,68,68,0.14)',
                color: isAvailable ? 'var(--sm-success-strong)' : 'var(--sm-danger-strong)'
            }}
        >
            <Box
                sx={{
                    position: 'relative',
                    width: 8,
                    height: 8,
                    borderRadius: '50%',
                    bgcolor: isAvailable ? 'var(--sm-success-bright)' : 'var(--sm-danger)',
                    '&::after': {
                        content: '""',
                        position: 'absolute',
                        inset: 0,
                        borderRadius: '50%',
                        bgcolor: isAvailable ? 'var(--sm-success-bright)' : 'var(--sm-danger)',
                        animation: `${statusPing} 1.8s ease-out infinite`
                    }
                }}
            />
            <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, lineHeight: 1 }}>
                {label}
            </Typography>
        </Box>
    );

    const renderTrackingButton = () => {
        const getButtonState = () => {
            if (hasTracking) {
                return {
                    variant: "outlined",
                    color: "secondary",
                    icon: <BookmarkIcon />,
                    text: "Untrack",
                    disabled: false,
                    tooltip: `Stop tracking this ${itemLabelDisplay}`
                };
            } else if (isPremium) {
                return {
                    variant: "contained",
                    color: "primary",
                    icon: <BookmarkBorderIcon />,
                    text: "Track",
                    disabled: false,
                    tooltip: `Get notified when this ${itemLabelDisplay} becomes available`
                };
            } else if (isAuthenticated) {
                return {
                    variant: "contained",
                    color: "primary",
                    icon: <BookmarkBorderIcon />,
                    text: "Upgrade to Track",
                    disabled: false,
                    tooltip: `Upgrade to premium to track this ${itemLabelDisplay}`
                };
            } else {
                return {
                    variant: "contained",
                    color: "primary",
                    icon: <BookmarkBorderIcon />,
                    text: "Sign In to Track",
                    disabled: false,
                    tooltip: `Sign in to track this ${itemLabelDisplay}`
                };
            }
        };

        const buttonState = getButtonState();

        return (
            <Box sx={{ mt: 2, mb: 2 }}>
                <Tooltip title={buttonState.tooltip}>
                    <span>
                        <Button
                            variant={buttonState.variant}
                            color={buttonState.color}
                            onClick={handleTrackingButton}
                            startIcon={buttonState.icon}
                            disabled={isTrackingLoading || buttonState.disabled}
                            fullWidth
                        >
                            {isTrackingLoading ? (
                                <CircularProgress size={24} color="inherit" />
                            ) : (
                                buttonState.text
                            )}
                        </Button>
                    </span>
                </Tooltip>

                {hasTracking && trackingData && (
                    <Box sx={{ mt: 2 }}>
                        <NotificationPreferences
                            tracking={trackingData}
                            onPreferenceChange={handlePreferenceChange}
                            onMaxPriceChange={isPremium ? handleMaxPriceChange : null}
                            disabled={updatingPreferences}
                        />
                    </Box>
                )}
            </Box>
        );
    };

    const renderMap = useCallback((mapData, availabilityLabel = 'Best Buy or Micro Center', retailers = null) => {
        // Without Premium: the count the API gives everyone, never which states (#392).
        // No product (still loading, or a SKU that does not exist) means nothing to say.
        if (!isPremium) {
            return stock?.sku ? (
                <LockedStateAvailability
                    overview={stateOverview?.sku === stock.sku ? stateOverview.data : null}
                    bestBuyInStore={!!stock.bestBuyInStoreAvailability}
                    bestBuyOnline={!!stock.bestBuyOnlineAvailability}
                    microCenterInStore={!!stock.mcOffer?.inStoreAvailability}
                    microCenterStale={!!stock.mcOffer?.availabilityStale}
                />
            ) : null;
        }

        if (isLoading) {
            return <div className={styles.loading}>Loading map data...</div>;
        }

        return <MapChart stateData={mapData} availabilityLabel={availabilityLabel} retailers={retailers} />;
    }, [isLoading, isPremium, stateOverview, stock]);

    // "Last checked" ticks while the page is open; the data itself is refetched by the page.
    const [now, setNow] = useState(() => Date.now());
    useEffect(() => {
        const id = setInterval(() => setNow(Date.now()), 1000);
        return () => clearInterval(id);
    }, []);

    const renderLastChecked = (value, pollSeconds, cadence) => {
        const checked = describeChecked(value, pollSeconds, { now });
        return (
            <div className={styles.offer_row}>
                <div className={styles.offer_label}>Last checked</div>
                <div className={styles.offer_value}>
                    {checked.text ? (
                        <span className={`${styles.checked_live} ${checked.stale ? styles.checked_stale : ''}`} title={cadence}>
                            <span className={styles.checked_dot} aria-hidden="true" />
                            {checked.text}
                        </span>
                    ) : 'Not yet'}
                </div>
            </div>
        );
    };

    const calculateDaysDifference = (dateValue, options = {}) => {
        const {
            minYear = 2000,
            maxDays = Number.POSITIVE_INFINITY
        } = options;

        if (!dateValue) {
            return "N/A";
        }
        
        try {
            let update;
            
            if (typeof dateValue === 'number') {
                const numericValue = dateValue < 1_000_000_000_000 ? dateValue * 1000 : dateValue;
                update = new Date(numericValue);
            } else if (dateValue && typeof dateValue === 'object' && !Array.isArray(dateValue)) {
                if (typeof dateValue.epochSecond === 'number') {
                    const millisFromEpoch = dateValue.epochSecond * 1000 + Math.floor((dateValue.nano || 0) / 1_000_000);
                    update = new Date(millisFromEpoch);
                } else {
                    update = new Date(dateValue);
                }
            } else if (Array.isArray(dateValue) && dateValue.length >= 3) {
                const [year, month, day] = dateValue;
                update = new Date(year, month - 1, day);
            } else {
                update = new Date(dateValue);
            }
            
            const now = new Date();
            
            if (isNaN(update.getTime())) {
                return "N/A";
            }

            if (update.getUTCFullYear() < minYear) {
                return "N/A";
            }
            
            const diffInMs = now - update;
            const diffInDays = Math.round(diffInMs / (1000 * 60 * 60 * 24));
            
            if (isNaN(diffInDays) || diffInDays < 0 || diffInDays > maxDays) {
                return "N/A";
            }
            
            return `${diffInDays} ${diffInDays === 1 ? 'day' : 'days'} ago`;
        } catch (error) {
            console.error("Error calculating date difference:", error);
            return "N/A";
        }
    };

    const calculateSavingsMeta = (regularValue, saleValue) => {
        const regular = Number(regularValue);
        const sale = Number(saleValue);

        if (!Number.isFinite(regular) || !Number.isFinite(sale) || regular <= 0 || sale >= regular) {
            return { amount: null, percent: null };
        }

        const amount = regular - sale;
        const percent = (amount / regular) * 100;

        return {
            amount: formatCurrency(amount),
            percent: formatPercent(percent)
        };
    };

    if (!stock) return null;

    const ghostCooldown = ghostCooldownRemaining(stock, currentTime);
    const bestBuyPricing = {
        regularPrice: stock.bestBuyRegularPrice ?? stock.regularPrice,
        salePrice: stock.bestBuySalePrice ?? stock.salePrice,
        onSale: stock.bestBuyOnSale ?? stock.onSale
    };
    const bestBuyDisplayPrice = formatCurrency(getDisplayPrice(bestBuyPricing));
    const bestBuyRegularPrice = formatCurrency(bestBuyPricing.regularPrice);
    const bestBuySavings = calculateSavingsMeta(bestBuyPricing.regularPrice, bestBuyPricing.salePrice);
    const mcDisplayPrice = formatCurrency(getDisplayPrice(stock?.mcOffer));
    const mcRegularPrice = formatCurrency(stock?.mcOffer?.regularPrice);
    const mcSavings = calculateSavingsMeta(stock?.mcOffer?.regularPrice, stock?.mcOffer?.salePrice);
    const bestBuyCheckoutUrl = stock.url
        || stock.addToCartUrl
        || (stock?.sku ? `https://www.bestbuy.com/site/searchpage.jsp?st=${stock.sku}` : null);
    const mcCheckoutUrl = stock?.mcOffer?.url
        || (stock?.mcOffer?.mcProductId
            ? `https://www.microcenter.com/product/${stock.mcOffer.mcProductId}`
            : null);
    const hasMcOffer = !!stock?.mcOffer;
    const neweggOffer = Array.isArray(stock?.retailerOffers)
        ? stock.retailerOffers.find((offer) => offer?.retailer === 'NEWEGG')
        : null;
    const hasNeweggOffer = !!neweggOffer;
    const neweggDisplayPrice = formatCurrency(getDisplayPrice(neweggOffer));
    const neweggRegularPrice = formatCurrency(neweggOffer?.regularPrice);
    const neweggSavings = calculateSavingsMeta(neweggOffer?.regularPrice, neweggOffer?.salePrice);
    const neweggCheckoutUrl = neweggOffer?.url || null;
    const mcInStoreUpdateText = calculateDaysDifference(stock?.mcOffer?.inStoreAvailabilityUpdatedAt, {
        minYear: 2020,
        maxDays: 3650
    });
    const isBestBuyAvailable = !!stock.bestBuyInStoreAvailability || !!stock.bestBuyOnlineAvailability;
    const isMicrocenterAvailable = !!(
        hasMcOffer && (
            stock?.mcOffer?.inStoreAvailability
            || stock?.mcOffer?.onlineAvailability
            || mcAvailableStores.length > 0
            || mcAvailableStateCodes.length > 0
        )
    );
    const isNeweggAvailable = !!neweggOffer?.onlineAvailability;
    const isBestBuyCheckoutEnabled = !!bestBuyCheckoutUrl && isBestBuyAvailable;
    const isMicrocenterCheckoutEnabled = !!mcCheckoutUrl && isMicrocenterAvailable;
    const isNeweggCheckoutEnabled = !!neweggCheckoutUrl && isNeweggAvailable;

    // When someone heads to a retailer without the extension, stop and ask —
    // every time, until they install it or check "Don't show this popup
    // again" (owner decision; Simplify's model). The click is intercepted
    // only when absence is proven (our origins, Chromium, probe answered);
    // Continue is always one click. probeExtension ran on mount.
    const persistNudgeOptOut = (dontShowAgain) => {
        if (!dontShowAgain) return;
        try { window.localStorage.setItem(NUDGE_OPT_OUT_KEY, '1'); } catch (e) { /* ignore */ }
    };
    const handleCheckoutClick = (event, url, retailerName) => {
        if (optedOutOfNudge()) return;
        if (!(extensionStatus && extensionStatus.installed === false)) return;
        event.preventDefault();
        setPendingCheckout({ url, retailerName });
        setShowExtensionPrompt(true);
    };
    const continueToRetailer = (dontShowAgain) => {
        persistNudgeOptOut(dontShowAgain);
        setShowExtensionPrompt(false);
        if (pendingCheckout?.url) {
            window.open(pendingCheckout.url, '_blank', 'noopener,noreferrer');
        }
    };
    const handlePromptGetExtension = (dontShowAgain) => {
        // The store opens via the button's own anchor; stay here so the
        // install lands.
        persistNudgeOptOut(dontShowAgain);
        setShowExtensionPrompt(false);
    };
    const handlePromptClose = (dontShowAgain) => {
        persistNudgeOptOut(dontShowAgain);
        setShowExtensionPrompt(false);
    };

    const persistTrackGateOptOut = (dontShowAgain) => {
        if (!dontShowAgain) return;
        try { window.localStorage.setItem(TRACK_GATE_OPT_OUT_KEY, '1'); } catch (e) { /* ignore */ }
    };
    // Tracking is worth having on its own — the email alerts are the free half
    // of the product — so this always tracks rather than dropping the click.
    const handleTrackAnyway = async (dontShowAgain) => {
        persistTrackGateOptOut(dontShowAgain);
        setTrackGateSkipped(true);
        setTrackGate(null);
        await performTracking();
    };
    // Installed-but-not-connected: hand the extension this session's token and
    // let it verify with the API (#234). A good answer tracks right away; only
    // a token the API refused goes back through sign-in, because that is the
    // one case sign-in fixes. Not-installed opens the store via the button's
    // own anchor.
    const handleTrackGatePrimary = async (dontShowAgain) => {
        persistTrackGateOptOut(dontShowAgain);
        const wasInstalled = Boolean(trackGate?.installed);
        if (!wasInstalled) {
            setTrackGate(null);
            return;
        }
        setTrackGateConnecting(true);
        let result;
        try {
            result = await connectExtension();
        } finally {
            setTrackGateConnecting(false);
        }
        if (result.ok) {
            setExtensionStatus({ installed: true, connected: true });
            setTrackGate(null);
            await performTracking();
            return;
        }
        if (result.reason === 'rejected' || result.reason === 'signed_out') {
            setTrackGate(null);
            localStorage.setItem('redirectAfterLogin', `${window.location.pathname}${window.location.search}`);
            login();
            return;
        }
        showSnackbar('Could not reach the extension. Reload the page and try again, or track anyway.', 'error');
    };
    const handleTrackGateClose = (dontShowAgain) => {
        persistTrackGateOptOut(dontShowAgain);
        setTrackGate(null);
    };

    const backTarget = location.state?.from || '/stocks';
    const getBackLabel = () => {
        if (!backTarget) {
            return 'Back';
        }
        if (backTarget.startsWith('/manufacturers/')) {
            const manufacturer = backTarget.split('/')[2];
            if (manufacturer) {
                return `Back to ${manufacturer} GPUs`;
            }
        }
        if (backTarget.startsWith('/my-tracked-stocks')) {
            return 'Back to Tracked Products';
        }
        if (backTarget.startsWith('/dates')) {
            return 'Back to New Releases';
        }
        if (backTarget.startsWith('/deals')) {
            return 'Back to Deals';
        }
        if (backTarget.startsWith('/stocks')) {
            return 'Back to Browse';
        }
        return 'Back';
    };

    return (
        <Box sx={{ maxWidth: "1200px", margin: "0 auto", padding: "24px" }}>
            <Button 
                startIcon={<ArrowBackIcon />}
                // Opened from a listing: tell it this is a return, so it scrolls back to this card (#390).
                onClick={() => navigate(backTarget, location.state?.from ? { state: { returnToCard: true } } : undefined)}
                sx={{ mb: 2 }}
            >
                {getBackLabel()}
            </Button>

            <div className={styles.container}>
                <div className={styles.container_left}>
                    <div className={styles.overview_panel}>
                        <img src={stock.image} alt={stock.alternateViewsImage} />

                        <h1 className={styles.overview_name}>{stock.name}</h1>
                        <div className={styles.overview_region}>{stock.sku}</div>

                        {renderTrackingButton()}
                    </div>
                    {isPremium && <StateAvailabilityPanel stateData={mergedStateData} />}
                </div>
                <div className={styles.container_right}>
                    <div className={styles.details_panel_plain}>
                        <div className={`${styles.offer_sections} ${styles.offer_sections_flush} ${(hasMcOffer || hasNeweggOffer) ? styles.offer_sections_two_col : ''}`}>
                            <div className={styles.offer_section}>
                                <div className={styles.offer_section_header}>
                                    <div className={styles.offer_section_brand}>
                                        <img
                                            src="/images/pics/Best_Buy_Logo.svg.png"
                                            alt="Best Buy"
                                            className={styles.offer_section_logo}
                                        />
                                        <span className={styles.offer_section_title}>Best Buy</span>
                                    </div>
                                </div>

                                <div className={styles.offer_row}>
                                    <div className={styles.offer_label}>Price</div>
                                    <div className={styles.offer_value}>
                                            {bestBuyPricing.onSale ? (
                                                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 0.2 }}>
                                                    <Typography sx={{ color: 'var(--sm-success)', fontWeight: 800 }}>
                                                        {bestBuyDisplayPrice || '—'}
                                                    </Typography>
                                                    {bestBuyRegularPrice && (
                                                        <Typography sx={{ color: 'var(--sm-muted-color)', textDecoration: 'line-through', fontSize: '0.9rem' }}>
                                                            {bestBuyRegularPrice}
                                                        </Typography>
                                                    )}
                                                    {(bestBuySavings.amount || bestBuySavings.percent) && (
                                                        <Typography sx={{ color: 'var(--sm-success)', fontSize: '0.82rem', fontWeight: 600 }}>
                                                            Save {bestBuySavings.amount || ''}{bestBuySavings.percent ? ` (${bestBuySavings.percent} off)` : ''}
                                                        </Typography>
                                                    )}
                                                </Box>
                                            ) : (
                                                bestBuyDisplayPrice || '—'
                                            )}
                                        </div>
                                    </div>

                                    <div className={styles.offer_row}>
                                        <div className={styles.offer_label}>In-store availability</div>
                                        <div className={styles.offer_value}>
                                            {renderAvailabilityPill(stock.bestBuyInStoreAvailability ? 'Available' : 'Unavailable', !!stock.bestBuyInStoreAvailability)}
                                        </div>
                                    </div>

                                    <div className={styles.offer_row}>
                                        <div className={styles.offer_label}>Online availability</div>
                                        <div className={styles.offer_value}>
                                            {renderAvailabilityPill(stock.bestBuyOnlineAvailability ? 'Available' : 'Unavailable', !!stock.bestBuyOnlineAvailability)}
                                        </div>
                                    </div>

                                    <div className={styles.offer_row}>
                                        <div className={styles.offer_label}>Last changed in store</div>
                                        <div className={styles.offer_value}>{calculateDaysDifference(stock.bestBuyInStoreAvailabilityUpdateDate ?? stock.inStoreAvailabilityUpdateDate)}</div>
                                    </div>

                                    <div className={styles.offer_row}>
                                        <div className={styles.offer_label}>Last changed online</div>
                                        <div className={styles.offer_value}>{calculateDaysDifference(stock.bestBuyOnlineAvailabilityUpdateDate ?? stock.onlineAvailabilityUpdateDate)}</div>
                                    </div>

                                    {renderLastChecked(stock.bestBuyLastChecked, POLL_SECONDS.bestBuy, 'Availability is checked every few minutes; price hourly')}

                                <div className={styles.offer_row}>
                                    <div className={styles.offer_label}>Link</div>
                                    <div className={styles.offer_value}>
                                        <Button
                                            component={bestBuyCheckoutUrl ? 'a' : 'button'}
                                            onClick={(e) => handleCheckoutClick(e, bestBuyCheckoutUrl, 'Best Buy')}
                                            target={bestBuyCheckoutUrl ? '_blank' : undefined}
                                            rel={bestBuyCheckoutUrl ? 'noreferrer' : undefined}
                                            href={bestBuyCheckoutUrl || undefined}
                                            variant="contained"
                                            disabled={!isBestBuyCheckoutEnabled}
                                            sx={{
                                                bgcolor: '#ffde00',
                                                color: '#111827',
                                                fontWeight: 700,
                                                '&:hover': { bgcolor: '#ffd000' },
                                                '&.Mui-disabled': {
                                                    bgcolor: 'var(--sm-border-strong)',
                                                    color: 'var(--sm-muted-color)'
                                                }
                                            }}
                                        >
                                            Best Buy - Checkout
                                        </Button>
                                    </div>
                                </div>
                            </div>

                            {hasMcOffer && (
                                <div className={styles.offer_section}>
                                    <div className={styles.offer_section_header}>
                                        <div className={styles.offer_section_brand}>
                                            <img
                                                src="/images/pics/microcenter-logo.jpeg"
                                                alt="Micro Center"
                                                className={styles.offer_section_logo}
                                            />
                                            <span className={styles.offer_section_title}>Micro Center</span>
                                        </div>
                                    </div>

                                    <div className={styles.offer_row}>
                                        <div className={styles.offer_label}>Price</div>
                                        <div className={styles.offer_value}>
                                            {stock.mcOffer.onSale ? (
                                                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 0.2 }}>
                                                    <Typography sx={{ color: 'var(--sm-success)', fontWeight: 800 }}>
                                                        {mcDisplayPrice || '—'}
                                                    </Typography>
                                                    {mcRegularPrice && (
                                                        <Typography sx={{ color: 'var(--sm-muted-color)', textDecoration: 'line-through', fontSize: '0.9rem' }}>
                                                            {mcRegularPrice}
                                                        </Typography>
                                                    )}
                                                    {(mcSavings.amount || mcSavings.percent) && (
                                                        <Typography sx={{ color: 'var(--sm-success)', fontSize: '0.82rem', fontWeight: 600 }}>
                                                            Save {mcSavings.amount || ''}{mcSavings.percent ? ` (${mcSavings.percent} off)` : ''}
                                                        </Typography>
                                                    )}
                                                </Box>
                                            ) : (
                                                mcDisplayPrice || '—'
                                            )}
                                        </div>
                                    </div>

                                    <div className={styles.offer_row}>
                                        <div className={styles.offer_label}>In-store availability</div>
                                        <div className={styles.offer_value}>
                                            {renderAvailabilityPill(stock.mcOffer.inStoreAvailability ? 'Available' : 'Unavailable', !!stock.mcOffer.inStoreAvailability)}
                                        </div>
                                    </div>

                                    <div className={styles.offer_row}>
                                        <div className={styles.offer_label}>Last changed in store</div>
                                        <div className={styles.offer_value}>{mcInStoreUpdateText}</div>
                                    </div>

                                    {renderLastChecked(stock.mcOffer.lastChecked, POLL_SECONDS.microCenter, 'Checked every 15 minutes across all stores')}

                                    <div className={styles.offer_row}>
                                        <div className={styles.offer_label}>Link</div>
                                        <div className={styles.offer_value}>
                                            <Button
                                                component={mcCheckoutUrl ? 'a' : 'button'}
                                            onClick={(e) => handleCheckoutClick(e, mcCheckoutUrl, 'Micro Center')}
                                                target={mcCheckoutUrl ? '_blank' : undefined}
                                            rel={mcCheckoutUrl ? 'noreferrer' : undefined}
                                            href={mcCheckoutUrl || undefined}
                                            variant="contained"
                                            disabled={!isMicrocenterCheckoutEnabled}
                                            sx={{
                                                bgcolor: '#1976d2',
                                                color: '#fff',
                                                fontWeight: 700,
                                                '&:hover': { bgcolor: '#1565c0' },
                                                '&.Mui-disabled': {
                                                    bgcolor: 'var(--sm-border-strong)',
                                                    color: 'var(--sm-muted-color)'
                                                }
                                            }}
                                        >
                                            Micro Center - Checkout
                                            </Button>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {hasNeweggOffer && (
                                <div className={styles.offer_section}>
                                    <div className={styles.offer_section_header}>
                                        <div className={styles.offer_section_brand}>
                                            <img
                                                src="/images/pics/newegg-logo.png"
                                                alt="Newegg"
                                                className={styles.offer_section_logo}
                                            />
                                            <span className={styles.offer_section_title}>Newegg</span>
                                        </div>
                                    </div>

                                    <div className={styles.offer_row}>
                                        <div className={styles.offer_label}>Price</div>
                                        <div className={styles.offer_value}>
                                            {neweggOffer.onSale ? (
                                                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 0.2 }}>
                                                    <Typography sx={{ color: 'var(--sm-success)', fontWeight: 800 }}>
                                                        {neweggDisplayPrice || '—'}
                                                    </Typography>
                                                    {neweggRegularPrice && (
                                                        <Typography sx={{ color: 'var(--sm-muted-color)', textDecoration: 'line-through', fontSize: '0.9rem' }}>
                                                            {neweggRegularPrice}
                                                        </Typography>
                                                    )}
                                                    {(neweggSavings.amount || neweggSavings.percent) && (
                                                        <Typography sx={{ color: 'var(--sm-success)', fontSize: '0.82rem', fontWeight: 600 }}>
                                                            Save {neweggSavings.amount || ''}{neweggSavings.percent ? ` (${neweggSavings.percent} off)` : ''}
                                                        </Typography>
                                                    )}
                                                </Box>
                                            ) : (
                                                neweggDisplayPrice || '—'
                                            )}
                                        </div>
                                    </div>

                                    <div className={styles.offer_row}>
                                        <div className={styles.offer_label}>Online availability</div>
                                        <div className={styles.offer_value}>
                                            {renderAvailabilityPill(neweggOffer.onlineAvailability ? 'Available' : 'Unavailable', !!neweggOffer.onlineAvailability)}
                                        </div>
                                    </div>

                                    <div className={styles.offer_row}>
                                        <div className={styles.offer_label}>Last changed online</div>
                                        <div className={styles.offer_value}>
                                            {calculateDaysDifference(neweggOffer.onlineAvailabilityUpdatedAt)}
                                        </div>
                                    </div>

                                    {renderLastChecked(neweggOffer.lastChecked, POLL_SECONDS.newegg, 'Checked every 15 minutes')}

                                    <div className={styles.offer_row}>
                                        <div className={styles.offer_label}>Link</div>
                                        <div className={styles.offer_value}>
                                            <Button
                                                component={neweggCheckoutUrl ? 'a' : 'button'}
                                            onClick={(e) => handleCheckoutClick(e, neweggCheckoutUrl, 'Newegg')}
                                                target={neweggCheckoutUrl ? '_blank' : undefined}
                                                rel={neweggCheckoutUrl ? 'noreferrer' : undefined}
                                                href={neweggCheckoutUrl || undefined}
                                                variant="contained"
                                                disabled={!isNeweggCheckoutEnabled}
                                                sx={{
                                                    bgcolor: '#F7941D',
                                                    color: '#111827',
                                                    fontWeight: 700,
                                                    '&:hover': { bgcolor: '#e28414' },
                                                    '&.Mui-disabled': {
                                                        bgcolor: 'var(--sm-border-strong)',
                                                        color: 'var(--sm-muted-color)'
                                                    }
                                                }}
                                            >
                                                Newegg - Checkout
                                            </Button>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                    <div className={styles.details_panel}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 2, pt: 2 }}>
                            <h4 className={styles.details_panel_heading} style={{ margin: 0 }}>State Availability</h4>
                            {ghostCooldown && (
                                <Tooltip title="Ghost listing detected.">
                                    <Typography
                                        variant="caption"
                                        sx={{
                                            color: 'var(--sm-warning)',
                                            fontWeight: 'bold',
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 0.5,
                                            cursor: 'help',
                                            marginLeft: 'auto'
                                        }}
                                    >
                                        👻 cooldown: {ghostCooldown}
                                    </Typography>
                                </Tooltip>
                            )}
                        </Box>
                        {renderMap(mergedStateData, 'Best Buy or Micro Center', {
                            bestBuy: {
                                displayPrice: bestBuyDisplayPrice,
                                checkoutUrl: bestBuyCheckoutUrl,
                                enabled: isBestBuyCheckoutEnabled
                            },
                            microCenter: {
                                displayPrice: mcDisplayPrice,
                                checkoutUrl: mcCheckoutUrl,
                                enabled: isMicrocenterCheckoutEnabled
                            },
                            onCheckout: handleCheckoutClick
                        })}
                    </div>
                </div>
            </div>

            <Snackbar
                open={snackbarOpen}
                autoHideDuration={6000}
                onClose={handleCloseSnackbar}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert
                    onClose={handleCloseSnackbar}
                    severity={snackbarSeverity}
                    sx={{ width: '100%' }}
                >
                    {snackbarMessage}
                </Alert>
            </Snackbar>
            <ExtensionPromptDialog
                open={showExtensionPrompt}
                retailerName={pendingCheckout?.retailerName}
                onGetExtension={handlePromptGetExtension}
                onContinue={continueToRetailer}
                onClose={handlePromptClose}
            />
            <TrackGateDialog
                open={Boolean(trackGate)}
                installed={Boolean(trackGate?.installed)}
                connecting={trackGateConnecting}
                onPrimary={handleTrackGatePrimary}
                onTrackAnyway={handleTrackAnyway}
                onClose={handleTrackGateClose}
            />
        </Box>
    );
};
